import "@hotwired/turbo-rails";
import "../controllers/shared";
import ahoy from "ahoy.js";
import ToastService from "./toast_service";
import "./sentry";
import "./posthog";
// Temporarily disable fix for slideout refresh
// import "./slideout_refresh_fix";

// A broadcast is delivered to every subscriber of the stream,
// and the subscribers are on different pages: an element named
// by the page that prompted the broadcast is simply absent on
// the next one. Turbo's own actions no-op there (their
// `targetElements` comes back empty and the loop over it runs
// zero times), so every custom action below opens with the same
// guard. Throwing instead abandons the action halfway and
// prints a TypeError on every page that was never the audience.
Turbo.StreamActions.reload_turbo_frame = function () {
  const turboFrameId = this.getAttribute("turbo_frame_id");
  const turboFrame = document.getElementById(turboFrameId);
  const newSrc = this.getAttribute("src");

  if (!turboFrame) return;

  if (newSrc) {
    turboFrame.src = newSrc;
  } else {
    turboFrame.reload();
  }
};

Turbo.StreamActions.scroll_to_bottom = function () {
  const target = this.getAttribute("target");
  const element = document.getElementById(target);

  if (!element) return;

  element.scrollTo({
    top: element.scrollHeight,
    behavior: "smooth",
  });
};

Turbo.StreamActions.visit = function () {
  Turbo.visit(this.getAttribute("location"));
};

Turbo.StreamActions.visit_url = function () {
  Turbo.visit(this.getAttribute("url"), { action: "replace" });
};

Turbo.StreamActions.reload_page = function () {
  window.location.reload();
};

Turbo.StreamActions.close_modal = function () {
  const modalId = this.getAttribute("modal_id");
  const modal = document.getElementById(modalId);

  if (!modal) return;

  modal.close();
};

// `getAttribute` answers a STRING or null, so every value
// that is present is truthy in a plain boolean test,
// `"false"` included. That is not theoretical here:
// `backdrop` is not one of Rails' boolean attributes, so
// the tag builder renders `backdrop: false` as the literal
// `backdrop="false"` instead of dropping it, and false is
// the helper's own default. Read the string.
const attributeIsTrue = (value) =>
  value !== null && value !== "false";

// Hand the request to `modal_controller` rather than
// reaching for the element. It is the only thing that
// knows the dialog is an admin preview, where `showModal()`
// would promote it to the browser's top layer, escaping
// `contain: layout` and breaking the `@container` queries
// that preview is built on. It is also the only thing that
// refuses a dialog which is already open or which a morph
// has taken out of the page, and the DOM answers both of
// those with an InvalidStateError that costs a replay.
//
// So `modal_id` here names the controller's
// `data-modal-id-value`, NOT the dialog's element id: the
// two differ almost everywhere (`..._modal` against
// `..._modal_content`). `close_modal` above still takes an
// element id, because `modal#close` carries no id filter
// and would otherwise shut every modal on the page.
//
// There is no missing-target guard for the same reason:
// there is no element to look up. `requestModalOpen` is
// window scoped, so a page carrying no modal with this id
// simply has nothing listening, which is the same no-op
// the guard buys every other action here.
Turbo.StreamActions.open_modal = function () {
  window.dispatchEvent(
    new CustomEvent("requestModalOpen", {
      detail: {
        id: this.getAttribute("modal_id"),
        displayBackdrop: attributeIsTrue(
          this.getAttribute("backdrop")
        ),
      },
    })
  );
};

Turbo.StreamActions.toast = function () {
  const text = this.getAttribute("text");
  const duration = this.getAttribute("duration");
  const gravity = this.getAttribute("gravity");
  const position = this.getAttribute("position");
  const close = this.getAttribute("close");
  const avatar = this.getAttribute("avatar");
  const type = this.getAttribute("type");
  const multiple = this.getAttribute("multiple");

  new ToastService().toast({
    text,
    duration,
    gravity,
    position,
    close,
    avatar,
    type,
    multiple,
  });
};

// CAL-501. TRANSPORT ONLY. Everything about the sound
// itself lives in the `new-order-sound` Stimulus
// controller, which listens for this event on the
// document. Keeping the two apart is what makes the
// controller unit testable: a Turbo stream action can
// only be driven by a real ActionCable delivery, a DOM
// event can be dispatched from a fixture.
//
// The order id is the only payload, and the controller
// needs it to stay silent when a delivery is repeated.
Turbo.StreamActions.new_order_sound = function () {
  document.dispatchEvent(
    new CustomEvent("new-order-sound:arrived", {
      detail: { orderId: this.getAttribute("order_id") },
    })
  );
};

Turbo.StreamActions.set_dataset_attributes = function () {
  const target = this.getAttribute("target");
  const attributes = JSON.parse(this.getAttribute("attributes"));
  const element = document.querySelector(target);

  if (!element) return;

  Object.entries(attributes).forEach(([key, value]) => {
    element.dataset[key] = value;
  });
};

Turbo.StreamActions.scroll_to_element = function () {
  const elementId = this.getAttribute("element_id");
  const element = document.getElementById(elementId);
  const behavior = this.getAttribute("behavior");
  const extraTop = this.getAttribute("extra_top");
  const rootElementSelector = this.getAttribute("root_element_selector");

  const scrollableElement =
    document.querySelector(rootElementSelector) ||
    document.querySelector("main");

  if (!element || !scrollableElement) return;

  // Calculate the element's position relative to the scrollable element
  const elementRect = element.getBoundingClientRect();
  const mainRect = scrollableElement.getBoundingClientRect();
  const relativeTop = elementRect.top - mainRect.top;

  // Scroll the scrollable element
  scrollableElement.scrollTo({
    top: scrollableElement.scrollTop + relativeTop - 100 - extraTop,
    behavior,
  });
};

// These are set on our routes.rb file. We are manually setting the engine's
// routes to "/cal-t" so that lists such as EasyPrivacy don't block us.
// https://github.com/easylist/easylist/blob/master/easyprivacy/easyprivacy_general.txt#L166-L167
ahoy.configure({
  visitsUrl: "/cal-t/visits",
  eventsUrl: "/cal-t/events",
  withCredentials: true,
});

// Tracking page views.
//
// This is disabled for now because we're doing it manually via a Stimulus
// controller or Rails controller.
//
// document.addEventListener(
//   "turbo:load",
//   ({ detail }) => {
//     ahoy.track("$view", {
//       url: detail.url,
//       page: detail.url.split("/").pop(),
//       title: document.title,
//       type: "load-page",
//     });
//   },
//   { once: true }
// );

// document.addEventListener("turbo:before-visit", ({ detail }) => {
//   if (detail.url === window.location.href) {
//     return;
//   }

//   ahoy.track("$view", {
//     url: detail.url,
//     page: detail.url.split("/").pop(),
//     title: document.title,
//     type: "visit-page",
//   });
// });

// document.addEventListener("turbo:frame-render", (event) => {
//   const detail = event.detail;
//   const fetchResponse = detail.fetchResponse;

//   ahoy.track("$view", {
//     url: fetchResponse.location.href,
//     page: fetchResponse.location.pathname,
//     title: document.title,
//     type: "turbo-frame",
//   });
// });
